import { addTermToFilter } from '../utils/searchUtils';
import VarType from 'Js/enums/varType.js';
import { FilterType } from '../types/filterType';
import { dateFormatDayTechnical } from 'Js/utils/dateFormat';
import { ref } from 'vue';
/**
 * Throws an error if filters is undefined
 * @param {Object} filters
 */
function _checkForFiltersDataProp(filters) {
    if (!filters)
        throw new Error('"filters" data prop not implemented');
}
/**
 * Determines whether the given filter is a nested filter (true) or not (false).
 * @param {Object} filter
 * @return {boolean}
 */
function _isNestedFilter(filter) {
    return filter.filterValue === undefined && filter.dataField === undefined && filter.value === undefined;
}
export function useFilterManagement(globalFilters, filterExpr, noLoading, columnState, filtersChanged, router, route, saveStateInUrl = true) {
    const filtersForStorage = ref({});
    /**
     * Creates filterExpression based on given filters.
     * Updated given storageFilters.
     */
    function createFilterExpression(filters, keysToBeIgnored) {
        // TODO(VHA) Rename, when all modules use integrated filter logic for AsyncDataGrid
        let filterExp = [];
        let nestedFilter = [];
        let nestedStorageFilters = {};
        function _setFilters(currentFilter, filterKey, isNested = false, filtersForStorage) {
            if (!currentFilter)
                return;
            if (currentFilter.shouldAddFilterToExpression?.()) {
                const devextremeStyleFilter = currentFilter.getDevExtremeStyleFilter();
                if (devextremeStyleFilter) {
                    const shouldFlattenNested = isNested && currentFilter.combine === false;
                    if (shouldFlattenNested) {
                        addTermToFilter(filterExp, devextremeStyleFilter);
                    }
                    else if (isNested) {
                        addTermToFilter(nestedFilter, devextremeStyleFilter, currentFilter.combineWith);
                    }
                    else {
                        addTermToFilter(filterExp, devextremeStyleFilter);
                    }
                }
            }
            const value = currentFilter.value;
            if (value || value === 0) {
                const storableFilter = {
                    value: value,
                    filterType: currentFilter.filterType ?? FilterType.Equal,
                };
                if (isNested) {
                    nestedStorageFilters[filterKey] = storableFilter;
                }
                else {
                    filtersForStorage[filterKey] = storableFilter;
                }
            }
        }
        for (const [filterKey, currentFilter] of Object.entries(filters)) {
            if (keysToBeIgnored && keysToBeIgnored.includes(filterKey) || currentFilter.isGetParam)
                continue;
            if (_isNestedFilter(currentFilter)) {
                for (const [nestedFilterKey, currentNestedFilter] of Object.entries(currentFilter)) {
                    _setFilters(currentNestedFilter, nestedFilterKey, true, filtersForStorage.value);
                }
                if (nestedFilter.length > 0) {
                    addTermToFilter(filterExp, nestedFilter);
                }
                if (Object.keys(nestedStorageFilters).length > 0) {
                    filtersForStorage.value[filterKey] = nestedStorageFilters;
                }
                nestedFilter = [];
                nestedStorageFilters = {};
            }
            else {
                _setFilters(currentFilter, filterKey, false, filtersForStorage.value);
            }
        }
        return filterExp;
    }
    function addTermToFilterUrl(filter, filterParams, key) {
        if (filter.value !== filter.resetValue && typeof (filter.value) !== VarType.Undefined) {
            if (Array.isArray(filter.value)) {
                if (filter.value.length > 0) {
                    filterParams[key] = JSON.stringify(filter.value);
                }
                return;
            }
            else if (typeof filter.value === VarType.Object) {
                filterParams[key] = dateFormatDayTechnical(filter.value, true);
            }
            else {
                filterParams[key] = filter.value;
            }
            if (filter.filterType !== FilterType.Equal && filter.filterType != null) {
                filterParams[key] = JSON.stringify({ value: filterParams[key], type: filter.filterType });
            }
        }
    }
    /**
     * Updates filterExpression based on current filters.
     * Saves filters to local forage if saveInLocalForage is true
     * @param {any} value - is ignored if filterKey is not passed
     * @param filter - filter which have to use preselected value
     */
    async function updateFilters(value, filter) {
        if (filter) {
            filter.selectedValue = value;
        }
        // It is required, because filterbuttons can handle filters before we want to send a request
        await nextTick();
        _checkForFiltersDataProp(globalFilters.value);
        const filterParams = {};
        if (saveStateInUrl) {
            // Set filters
            Object.keys(globalFilters.value).forEach(key => {
                if (!globalFilters.value[key].hasOwnProperty('value')) {
                    Object.keys(globalFilters.value[key]).forEach(key1 => {
                        addTermToFilterUrl(globalFilters.value[key][key1], filterParams, `${key}-${key1}`);
                    });
                }
                else {
                    addTermToFilterUrl(globalFilters.value[key], filterParams, key);
                }
            });
            // Set columns. The view is saved on every change.
            Object.entries(columnState?.toUrlParams?.() ?? {}).forEach(([key, value]) => {
                filterParams[key] = value;
            });
            // If new query is containing values OR old query had some
            const filterParamsChanged = Object.keys(filterParams).length > 0 && route?.name;
            const originalQueryIsNotEmpty = Object.keys(filterParams).length === 0 && route?.query && Object.keys(route?.query).length > 0;
            if (filterParamsChanged || originalQueryIsNotEmpty) {
                router?.push({ name: route.name, params: route?.params, query: filterParams, hash: route?.hash });
            }
        }
        filtersForStorage.value = {};
        if (typeof filtersChanged === 'function') {
            filtersChanged();
        }
        // TODO(VHA) Rename, when all modules use integrated filter logic for AsyncDataGrid
        filterExpr.value = createFilterExpression(globalFilters.value);
    }
    /**
     * Reset filters based on given filterKey or all filters if no filterKey is passed.
     */
    async function resetFilters(filterKey = null) {
        _checkForFiltersDataProp(globalFilters.value);
        if (filterKey) {
            // Reset a specific filter
            const targetFilter = globalFilters.value[filterKey];
            if (targetFilter) {
                if (_isNestedFilter(targetFilter)) {
                    for (const [_, currentNestedFilter] of Object.entries(targetFilter)) {
                        currentNestedFilter?.reset?.();
                    }
                }
                else {
                    targetFilter.reset();
                }
            }
            else {
                console.warn(`Filter with key "${filterKey}" not found.`);
            }
        }
        else {
            // Reset all filters
            for (const [_, currentFilter] of Object.entries(globalFilters.value)) {
                if (_isNestedFilter(currentFilter)) {
                    for (const [_, currentNestedFilter] of Object.entries(currentFilter)) {
                        currentNestedFilter.reset();
                    }
                }
                else {
                    currentFilter.reset();
                }
            }
        }
        await updateFilters();
    }
    /**
     * Loads filters from local forage and updates filterExpression.
     * userId has to be injected via Razor Page.
     * If no userId is present the filters are loaded and later saved not by user but universally
     */
    async function loadFiltersAndUpdate(filtersToOverwrite) {
        _checkForFiltersDataProp(globalFilters.value);
        if (filtersToOverwrite) {
            for (const [overwriteFilterKey, overwriteFilterValue] of Object.entries(filtersToOverwrite)) {
                if (overwriteFilterValue) {
                    const currentFilter = globalFilters.value[overwriteFilterKey];
                    if (currentFilter) {
                        currentFilter.setValue(overwriteFilterValue, currentFilter.filterType);
                    }
                }
            }
        }
        // TODO(VHA) Rename, when all modules use integrated filter logic for AsyncDataGrid
        noLoading.value = false;
        await updateFilters();
    }
    return {
        updateFilters,
        resetFilters,
        createFilterExpression,
        loadFiltersAndUpdate,
    };
}
